import {
	CircleAlertIcon,
	CircleCheckIcon,
	InfoIcon,
	Loader2Icon,
	TriangleAlertIcon,
} from "lucide-react";
import { useTheme } from "next-themes";
import { Toaster as Sonner, type ToasterProps } from "sonner";

/**
 * Toasts follow the Figma pill `toast · opening external` (2168:17581): a
 * near-black #0A0D14 slab at r12, 13px Geist Medium in white, an 18px icon at a
 * 10px gap, 11/16/11/14 padding, and a soft 0 6 16 shadow at 18% black. It is a
 * deliberately different object from shadcn's white card — the toast is the one
 * surface that floats over everything, so it reads as an overlay rather than as
 * one more panel in the stack.
 *
 * Semantics ride on the icon colour, not on repainting the slab, reusing the
 * brand hues from `partner/theme/colors.ts` (success #0E9F6E, warning #F97316,
 * error #F05252) so a toast matches the status badges already on screen.
 * `info` stays white, exactly as the Figma pill draws it.
 *
 * Error additionally carries a red hairline ring. The old skin gave failures a
 * whole red card, and the note behind it still holds — a failure must not read
 * like a confirmation — so dropping that card cannot also drop the signal; the
 * ring plus the red icon carries it inside the dark shell.
 */
const TOAST_CLASSNAMES = {
	toast:
		"group toast !w-fit !max-w-full !min-h-10 !items-center !gap-2.5 !rounded-[12px] !border-0 !bg-[#0a0d14] !py-[11px] !pr-4 !pl-[14px] !text-white !shadow-[0_6px_16px_0_rgba(0,0,0,0.18)] !font-sans",
	content: "!gap-0.5",
	title: "!text-[13px] !font-medium !leading-[17px] !text-white",
	description: "!text-[12px] !leading-4 !text-white/65",
	icon: "!m-0 !size-[18px] !shrink-0 [&>svg]:size-[18px]",
	// Sonner's action button is a light chip by default, which disappears
	// against the slab. White-on-transparent with a hairline keeps it legible
	// without competing with the message it sits next to.
	actionButton:
		"!ml-1 !h-6 !shrink-0 !rounded-md !border !border-white/20 !bg-white/10 !px-2 !text-[12px] !font-medium !text-white hover:!bg-white/20",
	cancelButton:
		"!ml-1 !h-6 !shrink-0 !rounded-md !border-0 !bg-transparent !px-2 !text-[12px] !font-medium !text-white/60 hover:!text-white",
	// Drawn as an outline, not a second box-shadow: the base slab already sets
	// one at the same specificity, so a shadow here is a source-order coin flip.
	// An outline also costs no layout, unlike a border.
	error: "!outline !outline-1 !-outline-offset-1 !outline-[#f05252]/45",
} as const;

const Toaster = ({ toastOptions, ...props }: ToasterProps) => {
	const { theme = "light" } = useTheme();

	return (
		<Sonner
			theme={theme as ToasterProps["theme"]}
			className="toaster group"
			icons={{
				success: <CircleCheckIcon className="size-[18px] text-[#0e9f6e]" />,
				info: <InfoIcon className="size-[18px] text-white" />,
				warning: <TriangleAlertIcon className="size-[18px] text-[#f97316]" />,
				error: <CircleAlertIcon className="size-[18px] text-[#f05252]" />,
				loading: (
					<Loader2Icon className="size-[18px] animate-spin text-white/70" />
				),
			}}
			toastOptions={{
				...toastOptions,
				classNames: {
					...TOAST_CLASSNAMES,
					...toastOptions?.classNames,
				},
			}}
			{...props}
		/>
	);
};

export { Toaster };
